import { Divider, withStyles } from '@material-ui/core'
import { authSelectors } from 'ducks/auth'
import CustomField from 'elements/field/CustomField'
import useTrackComponent from 'hooks/eventTracking/useTrackComponent'
import {
  StyledDialog as Dialog,
  StyledDialogActions,
  StyledDialogContent,
  StyledDialogTitle,
} from 'layout/StyledDialog'
import { debounce } from 'lodash'
import { Button, FormControl, MenuItem, Select, Typography } from 'opensolar-ui'
import { useErrorHandling } from 'ra-core'
import React, { MouseEventHandler, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ReferenceInput, useInput, useTranslate } from 'react-admin'
import { useField, useForm } from 'react-final-form'
import { useSelector } from 'react-redux'
import { makeOpenSolarStyles } from 'themes/makeOpenSolarStyles'
import { MOUNTING_SYSTEMS, MountingID, NativeMountingID } from 'types/mounting'
import { ActionType, EventType } from 'types/tracking'
import { useFeatureFlagDynamic } from 'util/featureFlags'
import { syncMounting } from '../nativeMounting/calculate'
import getNativeMountingSystems from '../nativeMounting/mountingSystems'

const useStyles = makeOpenSolarStyles((theme) => ({
  selector: {
    margin: '10px 0',
    backgroundColor: theme.greyLight3,
  },
  roofTypeNote: {
    color: theme.palette.text.secondary,
  },
  roofTypeSection: {
    paddingTop: 16,
  },
  roofTypeDivider: {
    marginBottom: 12,
  },
  dialogTitle: {
    paddingTop: '1.5rem',
    paddingBottom: '.5rem',
  },
  dialogContent: {
    paddingTop: '.5rem',
    paddingBottom: '.5rem',
  },
  dialogActions: {
    padding: '1rem',
    '& > :not(:first-child)': {
      marginLeft: '1rem',
    },
  },
}))

const StyledDialog = withStyles({
  paper: {
    minWidth: '40.625rem',
  },
})(Dialog)

enum DialogType {
  NONE = 'none',
  RACKING_CHANGE = 'racking_change',
  MULTI_SYSTEM = 'multi_system',
}

const DIALOG_MESSAGES = {
  [DialogType.RACKING_CHANGE]: 'Your racking solution will change. Do you wish to continue?',
  [DialogType.MULTI_SYSTEM]: 'This will change roof type for all systems in this project. Do you wish to continue?',
}

const RoofTypeChangeDialog = ({
  isOpen,
  dialogContent,
  onCancel,
  onConfirm,
}: {
  isOpen: boolean
  dialogContent: string
  onConfirm: MouseEventHandler
  onCancel: MouseEventHandler
}) => {
  const translate = useTranslate()
  const classes = useStyles()

  return (
    <StyledDialog open={isOpen} data-testid="roof_type_change_dialog">
      <StyledDialogTitle className={classes.dialogTitle}>{translate('Change roof type?')}</StyledDialogTitle>
      <StyledDialogContent className={classes.dialogContent}>{translate(dialogContent)}</StyledDialogContent>
      <StyledDialogActions className={classes.dialogActions}>
        <Button variant="contained" color="default" onClick={onCancel}>
          {translate('Discard changes')}
        </Button>
        <Button variant="contained" color="primary" onClick={onConfirm}>
          {translate('Save')}
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  )
}

const RoofTypeDropdown = (props) => {
  const classes = useStyles()
  const {
    allowEmpty,
    choices,
    emptyValue,
    emptyText,
    format,
    options,
    onBlur,
    onChange,
    onFocus,
    parse,
    resource,
    source,
    validate,
    disabled,
    ...rest
  } = props
  const { change } = useForm()
  const translate = useTranslate()
  const { trackEvent } = useTrackComponent({
    componentKey: 'roof_type_dropdown',
    eventName: 'Design - Change Roof Type',
  })

  const { id, input } = useInput({
    format,
    parse,
    resource,
    source,
    validate,
    ...rest,
  })

  const [dialogState, setDialogState] = useState({
    isOpen: false,
    type: DialogType.NONE,
    pendingValue: '',
  })

  const getDialogType = useCallback((currentValue: string, newValue: string): DialogType => {
    const system = window.editor.selectedSystem
    const systemCount = window.editor.getSystems().length
    const mountingSystem = system.mounting
    const changeWillResetRacking = mountingSystem && system.integration_json?.[mountingSystem]?.enabled

    if (!currentValue || currentValue === newValue) {
      return DialogType.NONE
    }

    if (changeWillResetRacking) {
      return DialogType.RACKING_CHANGE
    }

    if (systemCount > 1) {
      return DialogType.MULTI_SYSTEM
    }

    return DialogType.NONE
  }, [])

  const handleConfirm = useCallback(() => {
    const { pendingValue } = dialogState
    setDialogState({ isOpen: false, type: DialogType.NONE, pendingValue: '' })
    change(input.name, pendingValue)
  }, [dialogState.pendingValue, change, input.name])

  const handleCancel = useCallback(() => {
    setDialogState({ isOpen: false, type: DialogType.NONE, pendingValue: '' })
  }, [])

  const handleChange = useCallback(
    (e) => {
      e.preventDefault()
      trackEvent(EventType.USER_INTERACTION, { type: ActionType.SELECT }, { value: e.target.value })

      const newVal = e.target.value
      const dialogType = getDialogType(input.value, newVal)

      if (dialogType === DialogType.NONE) {
        change(input.name, newVal)
      } else {
        setDialogState({
          isOpen: true,
          type: dialogType,
          pendingValue: newVal,
        })
      }

      if (onChange) {
        onChange(e)
      }
    },
    [input.value, getDialogType, change, input.name, trackEvent, onChange]
  )

  // Update the input with our custom handlers
  input.onChange = handleChange
  input.onBlur = onBlur
  input.onFocus = onFocus

  const renderEmptyItemOption = useCallback(() => {
    return React.isValidElement(emptyText)
      ? React.cloneElement(emptyText)
      : emptyText === ''
        ? ' ' // em space, forces the display of an empty line of normal height
        : translate(emptyText, { _: emptyText })
  }, [emptyText, translate])

  return (
    <>
      <Select id={id} {...input} {...options} className={classes.selector} displayEmpty={true} disabled={disabled}>
        {allowEmpty ? (
          <MenuItem
            value={''}
            key="null"
            aria-label={translate('ra.action.clear_input_value')}
            title={translate('ra.action.clear_input_value')}
          >
            {renderEmptyItemOption()}
          </MenuItem>
        ) : null}
        {choices?.map((choice) => (
          <MenuItem key={choice.url} value={choice.url}>
            {translate(choice.name)}
          </MenuItem>
        ))}
      </Select>
      <RoofTypeChangeDialog
        dialogContent={DIALOG_MESSAGES[dialogState.type] || ''}
        isOpen={dialogState.isOpen}
        onCancel={handleCancel}
        onConfirm={handleConfirm}
      />
    </>
  )
}

const minimalMountingCalcInput = { panelBlocks: [], roof: { roofIndex: 0, roofTypeName: null }, options: {} }

export function shouldDisableRoofType(mountingId: string | undefined): boolean {
  if (!mountingId) return false
  if (!MOUNTING_SYSTEMS[mountingId as MountingID]?.types.includes('ground')) return false
  try {
    const systems = getNativeMountingSystems()
    const def = systems[mountingId as NativeMountingID]
    if (!def?.class) return true
    const instance = new def.class(minimalMountingCalcInput)
    const params = instance.getCompatibilityParameters?.()
    return params?.roofTypeRequired === false
  } catch {
    return false
  }
}

/**
 * Re-runs mounting compatibility for every system when the project roof type changes from the
 * design panel.
 *
 * For most native systems this recompute happens incidentally: the mounting `Inputs` component
 * resets its default values on a roof type change, which dispatches an `objectChanged` studio
 * signal that flows through `sync` -> `getCompatibility`. Systems that hide the standard inputs
 * (e.g. Van der Valk while suitability has not been selected yet) never mount that component, so
 * they would otherwise never re-evaluate compatibility and their warnings go stale. This explicit
 * listener makes the recompute reliable regardless of which inputs UI is rendered.
 */
const useSyncMountingOnRoofTypeChange = () => {
  const form = useForm()
  const {
    input: { value: roofType },
  } = useField('roof_type', { subscription: { value: true } })
  const errorHandling = useErrorHandling()
  const isFeatureEnabled = useFeatureFlagDynamic()
  const bomGeneratorContext = useMemo(() => ({ errorHandling, isFeatureEnabled }), [errorHandling, isFeatureEnabled])
  const hasMountedRef = useRef(false)

  const syncMountingDebounced = useMemo(
    () =>
      debounce((targetForm, system) => syncMounting(targetForm, system, bomGeneratorContext), 500, {
        leading: false,
        trailing: true,
      }),
    [bomGeneratorContext]
  )

  useEffect(() => {
    // Skip the initial mount so we only react to genuine roof type changes, not the initial value.
    if (!hasMountedRef.current) {
      hasMountedRef.current = true
      return
    }
    window.editor?.getSystems?.().forEach((system) => {
      syncMountingDebounced(form, system)
    })
  }, [roofType, syncMountingDebounced, form])

  useEffect(() => () => syncMountingDebounced.cancel(), [syncMountingDebounced])
}

const RoofTypeSelector = () => {
  const orgId = useSelector(authSelectors.getOrgId)
  const translate = useTranslate()
  const classes = useStyles()

  useSyncMountingOnRoofTypeChange()

  const mounting = typeof window !== 'undefined' ? window.editor?.selectedSystem?.mounting : undefined
  const disableRoofType = shouldDisableRoofType(mounting)
  const {
    input: { value: roofTypeValue },
  } = useField('roof_type', { subscription: { value: true } })
  const showMountingOptionsNote = !disableRoofType && !roofTypeValue

  return (
    <div className={classes.roofTypeSection}>
      <Divider className={classes.roofTypeDivider} />
      <FormControl variant="outlined" size="small" fullWidth={true}>
        <Typography variant="subtitle2" gutterBottom>
          {translate('Roof Type')}
        </Typography>
      {showMountingOptionsNote && (
        <Typography variant="body2" className={classes.roofTypeNote} gutterBottom>
          {translate('Select for mounting options')}
        </Typography>
      )}
      <CustomField
        component={ReferenceInput}
        resource="projects"
        source="roof_type"
        name="roof_type"
        reference="roof_types"
        perPage={1000}
        allowEmpty
        fullWidth
        style={{ width: '100%' }}
        filter={{
          fav: 1,
          org_id: orgId,
        }}
        id="roof_type_field"
        emptyText={<i style={{ color: '#aaa' }}>{translate('Select Roof Type')}</i>}
      >
        <RoofTypeDropdown id="roof_type_select_input" disabled={disableRoofType} />
      </CustomField>
    </FormControl>
    </div>
  )
}

export default RoofTypeSelector
